<template>
	<div id="app">
		<LoadingScreen :is-loading.sync="isLoading" />
		<AppsEmployeeApp v-if="isAppsEmployee" />
		<div v-else-if="getUser.id && !isGuestRoute" class="flex flex-col h-full">
			<NavigationBar :menu-opened="isMenuOpen" @toggleMenu="toggleMenu" />
			<div id="scrollbar" ref="scrollbar" class="flex-grow flex flex-col" :style="showOnboarding ? 'overflow: hidden' : ''">
				<div ref="sideBarMainWrapper" id="sideBarMainWrapper" class="flex-1 flex">
					<SideBar v-if="isMenuOpen || !$store.getters.getIsMobileView" class="self-stretch" :menu-opened="isMenuOpen" />
					<div v-show="scrollIconOpacity" class="absolute top-0 right-0 left-0 h-16" :style="{ opacity: scrollIconOpacity }">
						<div class="flex justify-center">
							<font-awesome-icon :icon="['fas', 'redo']" class="mt-8" v-if="scrollIconOpacity < 1"></font-awesome-icon>
							<font-awesome-icon :icon="['fas', 'redo']" class="mt-8" spin v-else></font-awesome-icon>
						</div>
					</div>
					<main
						class="relative flex flex-1 flex-col bg-white rounded shadow-md m-8 content"
						@click="closeMenu"
						:class="{ 'lg-main': !this.$store.getters.getIsMobileView }"
					>
						<router-view @scrollUp="scrollUp" @scrollDown="scrollDown" @setLoadingStatus="setLoadingStatus($event)" v-if="forceRefresh" />
						<div v-else>
							<LoadingSpinner />
						</div>
					</main>
				</div>
			</div>
			<OnboardingModal v-if="showOnboarding" />
			<HelpContact v-if="showHelpContact" />
			<PremiumContractModal v-if="shouldShowPremiumContractModal" @close="closePremiumContractModal" />
		</div>
		<router-view v-else-if="isGuestRoute" />
		<div class="h-screen w-screen flex items-center justify-center" v-else>
			<LoadingSpinner />
		</div>
		<portal-target name="modal-container" multiple />
	</div>
</template>

<script>
import SideBar from '@/components/navigation/Sidebar';
import NavigationBar from '@/components/navigation/NavigationBar';
import LoadingScreen from '@/components/navigation/LoadingScreen';
import Authentication from '@/mixins/Authentication';
import LoadingSpinner from '@/components/navigation/LoadingSpinner';
import AppsEmployeeApp from '@/apps/employee/App';
import tailwindConfig from '@/../tailwind.config';
import AppContainerMixin from '@/mixins/AppContainerMixin';
import OnboardingModal from '@/views/onboarding/OnboardingModal.vue';
import HelpContact from '@/views/help/HelpContact.vue';
import PremiumContractModal from '@/views/premiumpartners/PremiumContractModal.vue';

window.addEventListener('touchstart', function(evt) {
	const target = evt.touches.length > 0 ? evt.touches[0]?.target : undefined;
	const isSweetAlertButton = target.classList.contains('swal2-confirm') || target.classList.contains('swal2-cancel');
	const isSweetAlertContainer = target.classList.contains('swal2-container');
	const needsToBeClicked = isSweetAlertContainer || isSweetAlertButton;
	const isIPADPencil = evt.touches[0].touchType === 'stylus';

	if (target && needsToBeClicked && isIPADPencil) {
		evt.preventDefault();
		target.click();
	}
});

export default {
	mixins: [Authentication, AppContainerMixin],
	data() {
		return {
			isLoading: false,
			currentPage: this.$route.name,
			isMenuOpen: false,
			forceRefresh: true,
			scrollRefreshTimeoutId: 0,
			startedRefresh: false,
			scrollIconOpacity: 0,
			isPullToReloadBlocked: false,
			isPullToReloadBlockedTimeouteId: null,
			isPremiumContractModalDismissed: false,
		};
	},
	methods: {
		checkScreensize() {
			this.$store.commit('setIsMobileView', this.isAppContainer || window.innerWidth <= parseInt(tailwindConfig.theme.screens.xl));
		},
		toggleMenu($event) {
			if ($event) {
				this.openMenu();
			} else {
				this.closeMenu();
			}
		},
		openMenu() {
			this.isMenuOpen = true;
			this.$store.dispatch('updateChatsCount');
			window.document.documentElement.classList.add('disableScroll');
		},
		closeMenu() {
			if (this.isMenuOpen) {
				window.document.documentElement.classList.remove('disableScroll');
			}
			this.isMenuOpen = false;
		},
		scrollUp() {
			if (this.$refs.scrollbar && !this.$route.meta.disableScroll) {
				window.scrollTo(0, 0);
			}
		},
		scrollDown() {
			if (this.$refs.scrollbar) {
				this.$refs.scrollbar.scrollTo(0, this.$refs.scrollbar.scrollHeight);
			}
		},
		setLoadingStatus($event) {
			this.isLoading = $event.status;
		},
		closePremiumContractModal() {
			this.isPremiumContractModalDismissed = true;
			this.$store.commit('setConfirmLeaving', false);
			this.$store.dispatch('fetchMe');
		},
		addEventListener() {
			this.$root.$on('setLoadingStatus', status => {
				this.setLoadingStatus(status);
			});

			window.addEventListener('scroll', event => {
				if (this.isPullToReloadBlocked) {
					return;
				}

				const scrollY = (window.scrollY / window.innerHeight) * 100;
				const triggerLimit = -15; // %

				this.scrollIconOpacity = scrollY / triggerLimit;

				if (scrollY < triggerLimit && !this.startedRefresh) {
					clearTimeout(this.scrollRefreshTimeoutId);
					this.scrollRefreshTimeoutId = setTimeout(() => {
						if (scrollY < triggerLimit) {
							this.startedRefresh = true;
						}
					}, 40);
				} else if (scrollY >= triggerLimit && this.startedRefresh) {
					const confirmLeaving = this.$store.getters.getConfirmLeaving;
					if (confirmLeaving && !confirm(confirmLeaving)) {
						this.startedRefresh = false;
						clearTimeout(this.scrollRefreshTimeoutId);
						return;
					}

					this.forceRefresh = false;
					this.$store.commit('invalidateDamageCase');
					this.startedRefresh = false;
					setTimeout(() => {
						this.$nextTick(() => {
							this.forceRefresh = true;
						});
					}, 300);
				}
			});

			window.addEventListener('gesturechange', event => {
				clearTimeout(this.scrollRefreshTimeoutId);
				clearTimeout(this.isPullToReloadBlockedTimeouteId);
				this.isPullToReloadBlocked = true;
				this.isPullToReloadBlockedTimeouteId = setTimeout(() => {
					this.isPullToReloadBlocked = false;
				}, 1000);
			});
		},
	},
	created: function() {
		this.addEventListener();
		window.addEventListener('resize', this.checkScreensize);
		this.checkScreensize();
	},
	destroyed() {
		window.removeEventListener('resize', this.checkScreensize);
	},
	watch: {
		$route(to, from) {
			if (from.fullPath !== to.fullPath) {
				this.isMenuOpen = false;
			}

			this.currentPage = to.name;
			if (!to.meta.disableScroll && !from.meta.disableScroll) {
				this.scrollUp();
			}
		},
		isLocalAuthorizationExpired() {
			if (this.isLocalAuthorizationExpired) {
				this.$nextTick(() => {
					this.$router.push({ name: 'login' });
					this.$store.dispatch('logoutLocal');
				});
			}
		},
	},
	computed: {
		isGuestRoute() {
			return this.$route.meta?.guest;
		},
		isAppsEmployee() {
			return this.$route.path.includes('/apps/employee');
		},
		getAuthenticatedUserGroupLevel() {
			return this.$store.getters.getAuthenticatedUserGroupLevel;
		},
		showOnboarding() {
			return !this.$store.getters.getUser.onboarding.completed;
		},
		shouldShowPremiumContractModal() {
			if (this.isPremiumContractModalDismissed) {
				return false;
			}

			if (this.$store.getters.getUser.usergroup.level !== 3) {
				return false;
			}

			return (
				this.$store.getters.getUser.person.company.companyable?.is_premium_partner === 1 &&
				this.$store.getters.getUser.person.company.companyable?.premium_partner_contract_accept === 0
			);
		},
		isSafari() {
			return this.$browserDetect.isSafari;
		},
		showHelpContact() {
			return this.$store.getters.getIsShowHelpModal;
		},
	},
	components: {
		HelpContact,
		OnboardingModal,
		PremiumContractModal,
		AppsEmployeeApp,
		LoadingSpinner,
		LoadingScreen,
		NavigationBar,
		SideBar,
	},
};
</script>

<style lang="sass">
/* IMPORTS */
@import url('https://fonts.googleapis.com/css2?family=Source+Sans+Pro:ital,wght@0,200;0,300;0,400;0,600;0,700;0,900;1,200;1,300;1,400;1,600;1,700;1,900&display=swap')
@import './styles/schadenpro.sass'

/* GLOBAL STYLES */
html.disableScroll
	@apply fixed top-0 right-0 left-0 h-screen overflow-hidden

body,html
  background-color: $background_color

#scrollbar
  max-height: calc(100vh - #{$navbar_height} + 1px)

#app
	font-family: "Source Sans Pro", -apple-system, system-ui, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif, "Apple Color Emoji", "Segoe UI Emoji", "Segoe UI Symbol"
	-webkit-font-smoothing: antialiased
	-moz-osx-font-smoothing: grayscale
	text-align: center
	color: #2c3e50
	height: 100vh

.bg-sp
	background-color: $sp_blue

.border-sp
  border-color: $sp_blue

.color-sp
  color: $sp_blue

.btn
	@apply text-center
	@apply font-semibold
	@apply align-middle
	@apply cursor-pointer
	@apply select-none
	@apply border
	@apply rounded
	@apply border-solid
	@apply p-2
	min-width: 95px

	&-default
		@extend .btn
		@apply border-gray-400
		@apply bg-gray-200
		@apply text-gray-700

	&-blue
		@extend .btn
		background-color: $sp_blue
		border-color: $sp_blue
		color: white

.form-group
	@apply text-left mb-4 break-all
	color: #495057

	.multiselect__option

		&--highlight
			background: $sp_blue

			&::after
				background: $sp_blue

	div[class^="multiselect"]
		@apply border-gray-400

	label
		@apply inline-block
		@apply font-bold
		@apply mb-2

	input[type=text], input[type=password], textarea, input[type=number], input[type=tel], input[type=email]
    @extend input
    @apply w-full
    @apply block
    @apply border
    @apply rounded
    @apply shadow-sm
    @apply text-base
    @apply bg-white
    @apply font-normal
    @apply border-gray-400
    @apply px-2

	input[type="number"]
		-moz-appearance: textfield

	input[type="number"]::-webkit-inner-spin-button,
	input[type="number"]::-webkit-outer-spin-button
		-webkit-appearance: none
		margin: 0


	textarea
		@apply py-1 outline-none


.input
	@apply border
	@apply rounded

	&-error
		@extend .input
		@apply border-red-500

main
	background-clip: border-box

	.main
		@apply px-8 py-8

.input-errors
	@apply text-red-600

input:disabled:not(.vc-text-base), .multiselect--disabled, .multiselect--disabled .multiselect__tags
	background: #e9ecef

.btn:disabled
	opacity: 0.75
	pointer-events: none

.svg-inline--fa
  @apply transition-all duration-300

textarea
	@apply h-32
</style>
